/**
 * @file components/OfflineIndicator.js
 * @description Shows sync status in a bar at the top of the screen.
 * States:
 *   - Online (silent — no bar)
 *   - Online + Syncing: blue bar with progress
 *   - Offline: yellow bar
 *   - Offline + Pending: yellow bar with count
 *   - Sync Error: red bar
 *   - Sync Complete: green flash
 *   - Wave 2 §2.9: "Sign in again to finish syncing (N items)" (red, the queue
 *     is kept) and "Your sign-in ends in N min" (yellow, from T+23h of the
 *     24-hour session).
 * Also registers the service worker and initializes the sync engine.
 *
 * 2026-09-10 (James): the sign-in bars carry a real "Sign in again" button.
 * It opens the in-place re-sign-in card (components/ReSignIn.js) — a
 * DELIBERATE re-sign-in that asks for the password and mints a fresh 24-hour
 * session. It does NOT bend the standing rule: sessions are a 24-hour
 * absolute timeout with no sliding renewal, and nothing extends on a click
 * alone. The old wording said "— sync now", which read like a control it
 * never was; the words are now just the fact, and the button is the control.
 */
import React, { useState, useEffect, useRef } from "react";
import { initSyncEngine } from "../offline/syncEngine";
import { useOfflineSync } from "../offline/useOffline";
import { openReSignIn } from "./ReSignIn";

function OfflineIndicator() {
  const { isOnline, isSyncing, pendingCount, syncProgress, error, authRequired, sessionExpiresAt } = useOfflineSync();
  const [showComplete, setShowComplete] = useState(false);
  // Wave 2 §2.9: a once-a-minute tick so the deadline banner counts down.
  const [nowSec, setNowSec] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const t = setInterval(() => setNowSec(Math.floor(Date.now() / 1000)), 60000);
    return () => clearInterval(t);
  }, []);
  const minutesLeft = sessionExpiresAt ? Math.floor((sessionExpiresAt - nowSec) / 60) : null;
  const [initialized, setInitialized] = useState(false);
  const prevPendingRef = useRef(pendingCount);

  // Initialize sync engine and register service worker once
  useEffect(() => {
    if (initialized) return;
    setInitialized(true);

    // Register service worker
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {
        // SW registration failed — not critical
      });

      // Listen for sync trigger messages from SW
      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data && event.data.type === "TRIGGER_SYNC") {
          // The sync engine will handle this automatically
        }
      });
    }

    // Initialize sync engine
    const cleanup = initSyncEngine();
    return cleanup;
  }, [initialized]);

  // Show "sync complete" flash when pending goes from >0 to 0
  useEffect(() => {
    if (prevPendingRef.current > 0 && pendingCount === 0 && isOnline && !isSyncing) {
      setShowComplete(true);
      const timer = setTimeout(() => setShowComplete(false), 3000);
      return () => clearTimeout(timer);
    }
    prevPendingRef.current = pendingCount;
  }, [pendingCount, isOnline, isSyncing]);

  // Determine display state
  let barColor = "transparent";
  let textColor = "#FFFFFF";
  let message = "";
  let showBar = false;
  let dotColor = "";
  // The "Sign in again" button shows on exactly the two bars that are ABOUT
  // the sign-in — never on an offline / syncing / error bar, where a sign-in
  // would not help and would only be noise.
  let showSignInAgain = false;
  // Dark ink for the button label, chosen per bar colour so white-on-orange
  // (which is unreadable) never happens.
  let buttonInk = "#7A3E00";

  if (authRequired) {
    // Wave 2 §2.9: the session ended (24 h deadline or a sign-out) mid-queue.
    // Nothing was dropped; the flush resumes after the next sign-in.
    barColor = "#FF3B30";
    message = error || "Sign in again to finish syncing";
    showBar = true;
    dotColor = "#FF3B30";
    showSignInAgain = true;
    buttonInk = "#8A1008";
  } else if (error && isOnline) {
    // Sync error
    barColor = "#FF3B30";
    message = "Sync failed — will retry";
    showBar = true;
    dotColor = "#FF3B30";
  } else if (minutesLeft !== null && minutesLeft >= 0 && minutesLeft <= 60 && !isSyncing) {
    // Wave 2 §2.9: T+23h — the sign-in ends soon. The button is the way out.
    barColor = "#FF9500";
    message = "Your sign-in ends in " + minutesLeft + " min" + (pendingCount > 0 ? " (" + pendingCount + " pending)" : "");
    showBar = true;
    dotColor = "#FF9500";
    showSignInAgain = true;
    buttonInk = "#7A3E00";
  } else if (!isOnline && pendingCount > 0) {
    // Offline with pending changes
    barColor = "#FF9500";
    message = "Offline — " + pendingCount + " change" + (pendingCount !== 1 ? "s" : "") + " pending";
    showBar = true;
    dotColor = "#FF9500";
  } else if (!isOnline) {
    // Offline, no pending
    barColor = "#FF9500";
    message = "Working offline — changes will sync when connected";
    showBar = true;
    dotColor = "#FF9500";
  } else if (isSyncing) {
    // Online and syncing
    barColor = "#007AFF";
    message = "Syncing " + syncProgress.current + " of " + syncProgress.total + " items...";
    showBar = true;
    dotColor = "#007AFF";
  } else if (showComplete) {
    // Sync just completed
    barColor = "#34C759";
    message = "All changes synced";
    showBar = true;
    dotColor = "#34C759";
  }

  if (!showBar) return null;

  return (
    <div
      className="offline-indicator"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        padding: showSignInAgain ? "0.375rem 1rem" : "0.5rem 1rem",
        textAlign: "center",
        fontSize: "0.875rem",
        fontWeight: 600,
        fontFamily: "var(--font-family, -apple-system, system-ui, sans-serif)",
        color: textColor,
        backgroundColor: barColor,
        transition: "background-color 0.3s, opacity 0.3s",
        opacity: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: "0.5rem",
      }}
    >
      {/* Status dot */}
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: "#FFFFFF",
          display: "inline-block",
          animation: isSyncing ? "pulse 1.5s ease-in-out infinite" : "none",
        }}
      />
      {message}
      {/* The door: opens the in-place card, never a navigation, so nothing on
          screen is disturbed. 44 px tall so a gloved thumb can hit it. */}
      {showSignInAgain && (
        <button
          type="button"
          onClick={openReSignIn}
          data-testid="banner-sign-in-again"
          style={{
            minHeight: 44,
            padding: "0 1rem",
            border: "none",
            borderRadius: 10,
            backgroundColor: "#FFFFFF",
            color: buttonInk,
            fontFamily: "inherit",
            fontSize: "0.875rem",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Sign in again
        </button>
      )}
      {/* Sync progress bar */}
      {isSyncing && syncProgress.total > 0 && (
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            height: 2,
            backgroundColor: "rgba(255,255,255,0.5)",
            width: Math.round((syncProgress.current / syncProgress.total) * 100) + "%",
            transition: "width 0.3s",
          }}
        />
      )}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}

export default OfflineIndicator;
